/**
 * Asks the browser for notification permission when someone starts planning, so they hear when their
 * project is ready. Called synchronously from the send click or key press: browsers only show the prompt
 * for a user action, and work started from the landing page runs for minutes after the tab is left.
 */

/** Shared with usePushNotifications: 'true' once push is wanted on this browser, 'false' after turning it off. */
export const PUSH_SUBSCRIBED_KEY = 'push_notification_subscribed';
/** Fired when the prompt is granted; usePushNotifications then registers this browser for the signed-in user. */
export const PUSH_PERMISSION_GRANTED_EVENT = 'push-permission-granted';

const ASKED_AT_KEY = 'push_permission_asked_at';
/** A prompt closed without a choice is asked again, at most once a day (Chrome blocks after repeated closes). */
const ASK_AGAIN_AFTER_MS = 24 * 60 * 60 * 1000;
const ADMIN_USER_KEY = 'admin_user';

const webPushSupported = (): boolean => (
  typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  && !(window as any).__IS_RN_WEBVIEW__
);

const shouldAsk = (now: number): boolean => {
  if (!webPushSupported() || window.Notification.permission !== 'default') return false;
  // Turned off by the user, or an admin viewing as a user (this browser is the admin's).
  if (localStorage.getItem(PUSH_SUBSCRIBED_KEY) === 'false' || localStorage.getItem(ADMIN_USER_KEY)) return false;
  const askedAt = Number(localStorage.getItem(ASKED_AT_KEY) || 0);

  return !askedAt || now - askedAt >= ASK_AGAIN_AFTER_MS;
};

/** Show the browser's notification prompt if this browser has not answered it; never throws. */
export function askToNotifyOnPlanningStart(now = Date.now()): void {
  try {
    if (!shouldAsk(now)) return;
    localStorage.setItem(ASKED_AT_KEY, String(now));
    void window.Notification.requestPermission().then((permission) => {
      if (permission !== 'granted') return;
      localStorage.setItem(PUSH_SUBSCRIBED_KEY, 'true');
      window.dispatchEvent(new window.Event(PUSH_PERMISSION_GRANTED_EVENT));
    }).catch(() => {});
  } catch {
    // Storage or the Notification API can be unavailable (private mode, embedded views): planning goes on.
  }
}
